import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button, Dropdown, theme } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  EmailBlockType,
  IEmailBlock,
} from "../../../interfaces/emailTemplate.interface";
import {
  BLOCK_TYPE_LABELS,
  BLOCK_TYPES,
  newBlock,
} from "../emailTemplatesHelpers";
import { EditorBlock, newBlockId } from "../editorContent";
import BlockCard from "./BlockCard";

interface BlockListProps {
  blocks: EditorBlock[];
  onChange: (blocks: EditorBlock[]) => void;
}

const BlockList: React.FC<BlockListProps> = ({ blocks, onChange }) => {
  const { token } = theme.useToken();
  const sensors = useSensors(useSensor(PointerSensor));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    onChange(arrayMove(blocks, oldIndex, newIndex));
  };

  const updateBlock = (id: string, block: IEmailBlock) =>
    onChange(blocks.map((b) => (b.id === id ? { ...b, block } : b)));

  const removeBlock = (id: string) =>
    onChange(blocks.filter((b) => b.id !== id));

  const addBlock = (type: EmailBlockType) =>
    onChange([...blocks, { id: newBlockId(), block: newBlock(type) }]);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={blocks.map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          {blocks.map((entry) => (
            <BlockCard
              key={entry.id}
              id={entry.id}
              block={entry.block}
              onChange={(block) => updateBlock(entry.id, block)}
              onRemove={() => removeBlock(entry.id)}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Dropdown
        menu={{
          items: BLOCK_TYPES.map((type) => ({
            key: type,
            label: BLOCK_TYPE_LABELS[type],
            onClick: () => addBlock(type),
          })),
        }}
      >
        <Button
          icon={<PlusOutlined />}
          block
          style={{ marginTop: token.marginXS }}
        >
          Add block
        </Button>
      </Dropdown>
    </>
  );
};

export default BlockList;
